import React, { Suspense, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import type { LivenessSession } from "../../../components/face-liveness/FaceLivenessCheck";
import {
  enrollFaceApi,
  FaceEnrollmentState,
  fetchFaceEnrollmentPhotoApi,
  getFaceEnrollmentApi,
  removeFaceEnrollmentApi,
  startFaceEnrollmentApi,
} from "../api/kioskApi";
import { formatDateOnly } from "../utils/format";
import ReasonModal from "./ReasonModal";
import "../pages/Timekeeping.css";

const FaceLivenessCheck = React.lazy(() => import("../../../components/face-liveness/FaceLivenessCheck"));

type Props = { token: string; employeeId: string; employeeName: string };

/**
 * Rosto de referência do colaborador para o quiosque da loja (ADR 0031). Só o
 * DP cadastra, com o colaborador na frente da câmera deste computador ou
 * celular; a prova de vida da AWS confirma que é uma pessoa, não uma foto.
 */
export default function FaceEnrollmentCard({ token, employeeId, employeeName }: Props) {
  const [state, setState] = useState<FaceEnrollmentState | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [session, setSession] = useState<LivenessSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await getFaceEnrollmentApi(token, employeeId));
    } catch (e: any) {
      toast.error(e?.message || "Erro ao carregar o cadastro do rosto.");
    }
  }, [token, employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const enrollmentId = state?.enrollment?.id ?? null;
  useEffect(() => {
    if (!enrollmentId) return setPhotoUrl(null);
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchFaceEnrollmentPhotoApi(token, employeeId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPhotoUrl(objectUrl);
      })
      .catch(() => !cancelled && setPhotoUrl(null));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token, employeeId, enrollmentId]);

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      setSession(await startFaceEnrollmentApi(token, employeeId));
    } catch (e: any) {
      toast.error(e?.message || "Erro ao abrir a verificação.");
    } finally {
      setBusy(false);
    }
  };

  const finish = async (sessionId: string) => {
    setBusy(true);
    try {
      setState(await enrollFaceApi(token, employeeId, sessionId));
      setSession(null);
      toast.success("Rosto cadastrado.");
    } catch (e: any) {
      setSession(null);
      setError(e?.message || "Erro ao cadastrar o rosto.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const enrollment = state.enrollment;

  return (
    <article className="dpTkCard dpFaceCard">
      <div className="dpTkCard__head">
        <strong>Reconhecimento facial (quiosque)</strong>
        <span className={`dpBadge ${enrollment ? "dpBadge--green" : "dpBadge--gray"}`}>{enrollment ? "Cadastrado" : "Sem cadastro"}</span>
      </div>

      <div className="dpFaceCard__body">
        {enrollment ? (
          <div className="dpFaceCard__photo">{photoUrl ? <img src={photoUrl} alt={`Rosto cadastrado de ${employeeName}`} /> : null}</div>
        ) : null}
        <div>
          {enrollment ? (
            <p className="dpMuted">
              Cadastrado em {formatDateOnly(enrollment.enrolledAt)}
              {enrollment.enrolledBy ? ` por ${enrollment.enrolledBy.name}` : ""}.
            </p>
          ) : (
            <p className="dpMuted">Sem o rosto cadastrado, o colaborador só marca no quiosque pelos 4 dígitos do CPF, e cada marcação vai para conferência.</p>
          )}
          {!state.recognitionAvailable ? <p className="dpNotice dpNotice--warning">O reconhecimento facial não está configurado no servidor.</p> : null}
          {error ? <p className="dpNotice dpNotice--warning">{error}</p> : null}
          <div className="dpTkActions">
            <button type="button" className="dpBtn dpBtn--primary dpBtn--small" onClick={open} disabled={busy || !state.recognitionAvailable}>
              {enrollment ? "Refazer cadastro" : "Cadastrar rosto"}
            </button>
            {enrollment ? (
              <button type="button" className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setRemoving(true)} disabled={busy}>
                Remover
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {session ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpFaceModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Cadastrar rosto — {employeeName}</h2>
              <button type="button" className="dpModal__close" onClick={() => setSession(null)} disabled={busy}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <p className="dpNotice">Com o colaborador na frente da câmera, de frente, sem óculos escuros, boné ou máscara, num lugar com boa luz.</p>
              {busy ? (
                <p className="dpMuted">Cadastrando...</p>
              ) : (
                <Suspense fallback={<p className="dpMuted">Preparando a câmera...</p>}>
                  <FaceLivenessCheck
                    session={session}
                    onComplete={() => finish(session.sessionId)}
                    onCancel={() => setSession(null)}
                    onError={(message) => {
                      setSession(null);
                      setError(message);
                    }}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {removing ? (
        <ReasonModal
          title="Remover o rosto cadastrado?"
          description="O rosto é apagado do reconhecimento. Até um novo cadastro, o colaborador só marca no quiosque pelos 4 dígitos do CPF."
          confirmLabel="Remover"
          onClose={() => setRemoving(false)}
          onConfirm={async (reason) => {
            await removeFaceEnrollmentApi(token, employeeId, reason);
            toast.success("Rosto removido.");
            setRemoving(false);
            await load();
          }}
        />
      ) : null}
    </article>
  );
}
